import * as api from './api';
import { supabase } from '../supabaseClient';
import { getOrgId } from './orgStore';

// --- Commented out: DB triggers on item_movement_log now handle stock adjustments ---
// const fetchItemsByIds = async (itemIds) => {
//   if (itemIds.length === 0) return {};
//   const { data, error } = await supabase
//     .from('item')
//     .select('item_id, quantity_on_hand, cost_excl, is_service_item')
//     .in('item_id', itemIds);
//   if (error) throw error;
//   return Object.fromEntries((data || []).map((i) => [i.item_id, i]));
// };
//
// const adjustItemStock = async (lineItems, direction, updateCost = false) => {
//   const linesWithItems = lineItems.filter((l) => l.item_id);
//   if (linesWithItems.length === 0) return;
//
//   const itemIds = [...new Set(linesWithItems.map((l) => l.item_id))];
//   const itemMap = await fetchItemsByIds(itemIds);
//
//   const changes = {};
//   for (const line of linesWithItems) {
//     const item = itemMap[line.item_id];
//     if (!item || item.is_service_item) continue;
//     if (!changes[line.item_id]) changes[line.item_id] = { qty: 0, cost: null };
//     changes[line.item_id].qty += parseFloat(line.qty || 0);
//     if (updateCost) changes[line.item_id].cost = parseFloat(line.unit_price || 0);
//   }
//
//   await Promise.all(
//     Object.entries(changes).map(([itemId, { qty, cost }]) => {
//       const current = parseFloat(itemMap[itemId].quantity_on_hand || 0);
//       const payload = { quantity_on_hand: current + qty * direction };
//       if (cost !== null) payload.cost_excl = cost;
//       return supabase.from('item').update(payload).eq('item_id', itemId);
//     })
//   );
// };

/**
 * Fetch bills plus all the dependency data the Bill modal
 * and preview need (suppliers, items, taxes, setup, setup2).
 */
export const fetchBills = async (searchTerm = '') => {
  const orgId = getOrgId();
  if (!orgId) throw new Error('No org selected (org_id is null).');

  const [billRes, supplierRes, itemRes, taxRes, setupRes, setup2Res] =
    await Promise.all([
      supabase
        .from('bill')
        .select('*, supplier:supplier_id(supplier_name)')
        .eq('org_id', orgId)
        .order('bill_date', { ascending: false }),
      supabase.from('supplier').select('*').eq('org_id', orgId).order('supplier_id', { ascending: true }),
      supabase.from('item_with_stock').select('*').eq('org_id', orgId).order('item_id', { ascending: true }),
      supabase.from('tax').select('*').or(`org_id.eq.${orgId},is_global.eq.true`),
      supabase.from('setup').select('*').eq('org_id', orgId),
      supabase.from('setup2').select('*').eq('org_id', orgId),
    ]);

  if (billRes.error) throw billRes.error;
  if (supplierRes.error) throw supplierRes.error;
  if (itemRes.error) throw itemRes.error;
  if (taxRes.error) throw taxRes.error;
  if (setupRes.error) throw setupRes.error;
  if (setup2Res.error) throw setup2Res.error;

  let bills = (billRes.data || []).map((bill) => ({
    ...bill,
  }));

  // Client-side search on bill number or supplier name
  if (searchTerm) {
    const term = searchTerm.toLowerCase();
    bills = bills.filter(
      (b) =>
        b.bill_number?.toString().toLowerCase().includes(term) ||
        b.supplier?.supplier_name?.toLowerCase().includes(term) ||
        b.reference?.toLowerCase().includes(term)
    );
  }

  return {
    bills,
    suppliers: supplierRes.data || [],
    items: itemRes.data || [],
    taxes: taxRes.data || [],
    setup: setupRes.data || [],
    setup2: setup2Res.data || [],
  };
};

/**
 * Fetch line items for a specific bill (used when editing).
 */
export const fetchBillLines = async (billId) => {
  const { data, error } = await supabase
    .from('bill_line_items')
    .select('*')
    .eq('bill_id', billId)
    .order('line_number', { ascending: true });
  if (error) throw error;
  return data || [];
};

/**
 * Save a bill (create or update).
 *
 * @param {{ header: object, lines: object[] }} payload
 * @param {number|null} existingBillId – null for create
 */
export const saveBill = async ({ header, lines }, existingBillId = null) => {
  const orgId = getOrgId();
  if (!orgId) throw new Error('No org selected (org_id is null).');

  let billId = existingBillId;

  // Bill number (supplier invoice no.) is required
  if (!header.bill_number?.trim()) {
    throw new Error('Invoice number is required.');
  }

  // Check for duplicate bill number (exclude the bill being edited)
  const dupQuery = supabase
    .from('bill')
    .select('bill_id')
    .eq('bill_number', header.bill_number.trim())
    .eq('org_id', orgId);
  if (existingBillId) dupQuery.neq('bill_id', existingBillId);
  const { data: existing } = await dupQuery.maybeSingle();
  if (existing) {
    throw new Error(`Invoice number "${header.bill_number}" is already used by an existing bill.`);
  }

  // --- Stock reversal now handled by DB triggers on item_movement_log ---
  // if (existingBillId) {
  //   const oldLines = await fetchBillLines(existingBillId);
  //   await adjustItemStock(oldLines, -1);
  // }

  if (existingBillId) {
    // --- UPDATE ---
    const { error } = await supabase
      .from('bill')
      .update(header)
      .eq('bill_id', existingBillId);
    if (error) throw error;
  } else {
    // --- CREATE ---
    const { bill_id: _strip, ...insertPayload } = header;
    const { data, error } = await supabase
      .from('bill')
      .insert([{ ...insertPayload, org_id: orgId }])
      .select()
      .single();
    if (error) throw error;
    billId = data.bill_id;
  }

  // --- SAVE LINE ITEMS (full-replace strategy) ---
  await supabase.from('bill_line_items').delete().eq('bill_id', billId);

  const linesToInsert = lines
    .map((line, idx) => ({
      bill_id: billId,
      org_id: orgId,
      line_number: idx + 1,
      bill_date: header.bill_date || null,
      bill_number: header.bill_number,
      item_id: line.item_id || null,
      item_description: line.item_description,
      qty: line.quantity,
      unit_price: line.unit_price,
      cost_excl: line.unit_price,
      line_tax_amount: line.line_tax_amount || 0,
      tax_id: line.tax_rate_id || null,
      tax_rate: line.tax_rate != null ? line.tax_rate : null,
      line_total_inclusive_tax: line.total_price,
    }))
    .filter((l) => l.item_description || l.unit_price > 0);

  if (linesToInsert.length > 0) {
    const { error: insertError } = await supabase
      .from('bill_line_items')
      .insert(linesToInsert);
    if (insertError) throw insertError;
  }

  // --- Stock increase now handled by DB triggers on item_movement_log ---
  // await adjustItemStock(linesToInsert, +1, true);

  return billId;
};

/**
 * Delete a bill and its line items.
 */
export const deleteBill = async (billId) => {
  // --- Stock reversal now handled by DB triggers on item_movement_log ---
  // const oldLines = await fetchBillLines(billId);
  // await adjustItemStock(oldLines, -1);

  await supabase.from('bill_line_items').delete().eq('bill_id', billId);
  await api.deleteRecord('bill', 'bill_id', billId);
};
